import type { SWOOptions } from "../swo";

export interface SWOElements {
    editorPane?: HTMLElement;
    codeEditorContainer?: HTMLElement;
    codePrettierBtn?: HTMLElement;
    resizeHandle?: HTMLElement;
    rightPane?: HTMLElement;
    previewPaneContainer?: HTMLElement;
    previewDevicesContainer?: HTMLElement;
    previewFrame?: HTMLIFrameElement;
    resizeHandleConsole?: HTMLElement;
    consoleContainer?: HTMLElement;
    consoleOutput?: HTMLElement;
    clearConsoleBtn?: HTMLElement;
    previewFrameCover?: HTMLElement;
    toggleCodeEditorBtn?: HTMLElement;
    toggleConsoleBtn?: HTMLElement;
    resizeDesktopBtn?: HTMLElement;
    resizeTabletBtn?: HTMLElement;
    resizeMobileBtn?: HTMLElement;
    refreshPreviewBtn?: HTMLElement;
    openNewTabBtn?: HTMLElement;
    codeEditorUIElements?: NodeListOf<Element>;
    consoleUIElements?: NodeListOf<Element>;
}

export class UIManager {
    private static activeResizeManager: UIManager | null = null;
    elements: SWOElements = {};

    private targetElement: HTMLElement;
    private isResizingPanes: boolean = false;
    private isResizingConsole: boolean = false;

    private _initialPointerPosition: number = 0;
    private _initialEditorSize: number = 0;
    private readonly isVertical: boolean;
    private readonly isReversed: boolean;
    private readonly headless: boolean;
    private readonly primaryColor: string | undefined;
    private panePointerId: number | null = null;
    private consolePointerId: number | null = null;
    private previousCursor: string = "";
    private previousUserSelect: string = "";

    private _initialMouseYConsole: number = 0;
    private _initialPreviewHeight: number = 0;

    constructor(targetElement: HTMLElement, options: Pick<SWOOptions, "layout" | "layoutDirection" | "headless" | "primaryColor"> = {}) {
        this.targetElement = targetElement;
        this.isVertical = options.layout === "vertical";
        this.isReversed = options.layoutDirection === "reverse";
        this.headless = options.headless === true;
        this.primaryColor = options.primaryColor;
    }

    createUI() {
        this.targetElement.innerHTML = `
            <div class="swo-main-wrapper${this.isVertical ? " swo-layout-vertical" : ""}${this.isReversed ? " swo-layout-reverse" : ""}${this.headless ? " swo-headless" : ""}">
                <section class="swo-panel-editor-preview">
                    <div class="swo-editor-pane">
                        <div class="swo-editor-wrapper">
                            <div class="swo-code-editor-container"></div>
                        </div>
                        <button type="button" class="swo-code-prettier-btn" aria-label="Format code"><ss-icon icon="sparkles" thickness="2.2"></ss-icon> Format</button>
                    </div>

                    <div class="swo-resize-handle" role="separator" aria-orientation="vertical" aria-label="Resize editor and preview panes" aria-valuemin="0" aria-valuemax="100" aria-valuenow="50" tabindex="0"></div>

                    <div class="swo-right-pane">
                        <div class="swo-preview-pane-container">
                            <div class="swo-preview-devices-container">
                                <iframe class="swo-preview-frame" title="Live preview" sandbox="allow-scripts allow-forms allow-modals allow-popups"></iframe>
                            </div>
                        </div>

                        <div class="swo-resize-handle-console" role="separator" aria-orientation="horizontal" aria-label="Resize preview and console panes" aria-valuemin="0" aria-valuemax="100" aria-valuenow="66" tabindex="0"></div>

                        <div class="swo-console-container">
                            <div class="swo-console-header">
                                <span class="swo-console-title">Console</span>
                                <button type="button" class="swo-clear-console-btn" aria-label="Clear console">Clear</button>
                            </div>
                            <div class="swo-console-output" role="log" aria-label="Preview console output" aria-live="polite"></div>
                        </div>

                        <div class="swo-preview-frame-cover-resizeable swo-hidden"></div>
                    </div>
                </section>
                <section class="swo-panel-control">
                    <div class="swo-panel-control-group-left">
                        <button type="button" class="swo-control-button swo-resize-desktop" title="Desktop View" aria-label="Desktop view" aria-pressed="false">
                            <ss-icon icon="computer-desktop" thickness="2.2"></ss-icon>
                        </button>
                        <button type="button" class="swo-control-button swo-resize-tablet" title="Tablet View" aria-label="Tablet view" aria-pressed="false">
                            <ss-icon icon="device-tablet" thickness="2.2"></ss-icon>
                        </button>
                        <button type="button" class="swo-control-button swo-resize-mobile" title="Mobile View" aria-label="Mobile view" aria-pressed="false">
                            <ss-icon icon="device-phone-mobile" thickness="2.2"></ss-icon>
                        </button>
                    </div>
                    <div class="swo-panel-control-group-center">
                        <button type="button" class="swo-control-button swo-refresh-preview" title="Refresh Preview" aria-label="Refresh preview">
                            <ss-icon icon="arrow-path" thickness="2.2"></ss-icon>
                        </button>
                    </div>
                    <div class="swo-panel-control-group-right">
                        <button type="button" class="swo-control-button swo-toggle-code-editor-btn" title="Toggle Code Editor" aria-label="Toggle code editor" aria-pressed="true">
                            <ss-icon icon="code" thickness="2.2"></ss-icon>
                        </button>
                        <button type="button" class="swo-control-button swo-toggle-console-btn" title="Toggle Console" aria-label="Toggle console" aria-pressed="false">
                            <ss-icon icon="console" thickness="2.2"></ss-icon>
                        </button>
                        <button type="button" class="swo-control-button swo-open-new-tab" title="Open in New Tab" aria-label="Open preview in new tab">
                            <ss-icon icon="bolt" thickness="2.2"></ss-icon>
                        </button>
                    </div>
                </section>
            </div>
        `;
        if (typeof this.primaryColor === "string" && CSS.supports("color", this.primaryColor)) {
            const wrapper = this.targetElement.querySelector<HTMLElement>(".swo-main-wrapper");
            wrapper?.style.setProperty("--swo-primary-color", this.primaryColor);
        }
        if (this.headless) {
            this.targetElement.querySelectorAll(".swo-panel-control, .swo-code-prettier-btn, .swo-console-header").forEach(element => element.remove());
        }
        this.cacheElements();
        if (this.isReversed) {
            const el = this.elements;
            const panel = el.editorPane?.parentElement;
            if (panel && el.editorPane && el.rightPane && el.resizeHandle) {
                panel.insertBefore(el.rightPane, el.editorPane);
                panel.insertBefore(el.resizeHandle, el.editorPane);
            }
        }
        this.elements.resizeHandle?.setAttribute("aria-orientation", this.isVertical ? "horizontal" : "vertical");
        this.initResizeListeners();
    }

    cacheElements() {
        const T = this.targetElement;
        this.elements.editorPane = T.querySelector(".swo-editor-pane") as HTMLElement;
        this.elements.codeEditorContainer = T.querySelector(".swo-code-editor-container") as HTMLElement;
        this.elements.codePrettierBtn = T.querySelector(".swo-code-prettier-btn") as HTMLElement;
        this.elements.resizeHandle = T.querySelector(".swo-resize-handle") as HTMLElement;

        this.elements.rightPane = T.querySelector(".swo-right-pane") as HTMLElement;
        this.elements.previewPaneContainer = T.querySelector(".swo-preview-pane-container") as HTMLElement;
        this.elements.previewDevicesContainer = T.querySelector(".swo-preview-devices-container") as HTMLElement;
        this.elements.previewFrame = T.querySelector(".swo-preview-frame") as HTMLIFrameElement;

        this.elements.resizeHandleConsole = T.querySelector(".swo-resize-handle-console") as HTMLElement;
        this.elements.consoleContainer = T.querySelector(".swo-console-container") as HTMLElement;
        this.elements.consoleOutput = T.querySelector(".swo-console-output") as HTMLElement;
        this.elements.clearConsoleBtn = T.querySelector(".swo-clear-console-btn") as HTMLElement;
        this.elements.previewFrameCover = T.querySelector(".swo-preview-frame-cover-resizeable") as HTMLElement;

        this.elements.toggleCodeEditorBtn = T.querySelector(".swo-toggle-code-editor-btn") as HTMLElement;
        this.elements.toggleConsoleBtn = T.querySelector(".swo-toggle-console-btn") as HTMLElement;

        this.elements.resizeDesktopBtn = T.querySelector(".swo-resize-desktop") as HTMLElement;
        this.elements.resizeTabletBtn = T.querySelector(".swo-resize-tablet") as HTMLElement;
        this.elements.resizeMobileBtn = T.querySelector(".swo-resize-mobile") as HTMLElement;
        this.elements.refreshPreviewBtn = T.querySelector(".swo-refresh-preview") as HTMLElement;
        this.elements.openNewTabBtn = T.querySelector(".swo-open-new-tab") as HTMLElement;

        this.elements.codeEditorUIElements = T.querySelectorAll(".swo-editor-pane, .swo-resize-handle");
        this.elements.consoleUIElements = T.querySelectorAll(".swo-console-container, .swo-resize-handle-console");
    }

    initialLayout() {
        if (!this.elements.editorPane || !this.elements.consoleUIElements || !this.elements.previewPaneContainer || !this.elements.rightPane) return;

        this._onPaneResizeDoubleClick();

        // Hide console initially
        this.elements.consoleUIElements.forEach((element) =>
            element.classList.add("swo-hidden")
        );
        this.updateButtonActiveState(this.elements.toggleConsoleBtn, false);
        this.elements.previewPaneContainer.style.height = "100%";

        this.updateButtonActiveState(this.elements.toggleCodeEditorBtn, true);
    }

    updateButtonActiveState(button: HTMLElement | undefined, isActive: boolean) {
        if (!button) return;
        if (isActive) {
          button.classList.add("swo-active");
        } else {
          button.classList.remove("swo-active");
        }
        button.setAttribute("aria-pressed", String(isActive));
    }

    toggleConsole() {
        this._endConsoleResize();
        const el = this.elements;
        if (!el.consoleContainer || !el.consoleUIElements || !el.rightPane || !el.previewPaneContainer || !el.resizeHandleConsole) return;

        const consoleIsCurrentlyVisible = !el.consoleContainer.classList.contains("swo-hidden");

        el.consoleUIElements.forEach((element) => {
            element.classList.toggle("swo-hidden", consoleIsCurrentlyVisible);
        });

        const isConsoleVisibleAfterToggle = !consoleIsCurrentlyVisible;
        this.updateButtonActiveState(el.toggleConsoleBtn, isConsoleVisibleAfterToggle);

        const resizeHandleConsoleHeight = el.resizeHandleConsole.offsetHeight;

        if (isConsoleVisibleAfterToggle) {
            el.resizeHandleConsole.setAttribute("aria-valuenow", "66");
            el.previewPaneContainer.style.height = `calc(66% - ${resizeHandleConsoleHeight * 0.66}px)`;
            el.consoleContainer.style.height = `calc(34% - ${resizeHandleConsoleHeight * 0.34}px)`;
        } else {
            el.previewPaneContainer.style.height = "100%";
        }
    }

    toggleCodeEditor() {
        this._endPaneResize();
        const el = this.elements;
        if (!el.codeEditorUIElements) return;

        let isEditorVisibleAfterToggle = false;
        el.codeEditorUIElements.forEach((element) => {
            const isCurrentlyHidden = element.classList.contains("swo-hidden");
            element.classList.toggle("swo-hidden", !isCurrentlyHidden);

            if (element === el.editorPane) {
                isEditorVisibleAfterToggle = isCurrentlyHidden; // If it was hidden, it is now visible
            }
        });
        this.updateButtonActiveState(el.toggleCodeEditorBtn, isEditorVisibleAfterToggle);
    }

    initResizeListeners() {
        if (this.elements.resizeHandle) {
            this.elements.resizeHandle.addEventListener("pointerdown", this._onPaneResizePointerDown);
            this.elements.resizeHandle.addEventListener("pointermove", this._onPaneResizePointerMove);
            this.elements.resizeHandle.addEventListener("pointerup", this._onPaneResizePointerUp);
            this.elements.resizeHandle.addEventListener("pointercancel", this._onPaneResizePointerCancel);
            this.elements.resizeHandle.addEventListener("lostpointercapture", this._onPaneResizePointerCancel);
            this.elements.resizeHandle.addEventListener("keydown", this._onPaneResizeKeyDown);
            this.elements.resizeHandle.addEventListener("dblclick", this._onPaneResizeDoubleClick);
        }
        if (this.elements.resizeHandleConsole) {
            this.elements.resizeHandleConsole.addEventListener("pointerdown", this._onConsoleResizePointerDown);
            this.elements.resizeHandleConsole.addEventListener("pointermove", this._onConsoleResizePointerMove);
            this.elements.resizeHandleConsole.addEventListener("pointerup", this._onConsoleResizePointerUp);
            this.elements.resizeHandleConsole.addEventListener("pointercancel", this._onConsoleResizePointerCancel);
            this.elements.resizeHandleConsole.addEventListener("lostpointercapture", this._onConsoleResizePointerCancel);
            this.elements.resizeHandleConsole.addEventListener("keydown", this._onConsoleResizeKeyDown);
        }
    }

    // --- Pane Resizing Logic (Pointer Events: mouse, touch, and pen; capture
    // retargets move/up/cancel to the handle, so no document-level listeners
    // are needed and cancellation is handled explicitly) ---
    private _onPaneResizePointerDown = (e: PointerEvent) => {
        if (!this.elements.editorPane || !this.elements.previewFrameCover) return;
        if (e.button !== 0 || this.isResizingPanes || this.isResizingConsole) return;

        this._claimResize();
        e.preventDefault();
        this.isResizingPanes = true;
        this.panePointerId = e.pointerId;
        this.previousCursor = document.body.style.cursor;
        this.previousUserSelect = document.body.style.userSelect;
        this._initialPointerPosition = this.isVertical ? e.clientY : e.clientX;
        this._initialEditorSize = this.isVertical ? this.elements.editorPane.offsetHeight : this.elements.editorPane.offsetWidth;
        this.elements.previewFrameCover.classList.remove("swo-hidden");
        document.body.style.cursor = this.isVertical ? "row-resize" : "col-resize";
        document.body.style.userSelect = "none";
        try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { this._endPaneResize(); }
    };

    private _onPaneResizePointerMove = (e: PointerEvent) => {
        if (!this.isResizingPanes || e.pointerId !== this.panePointerId) return;
        const pointerPosition = this.isVertical ? e.clientY : e.clientX;
        const direction = this.isReversed ? -1 : 1;
        this._applyPaneSize(this._initialEditorSize + (pointerPosition - this._initialPointerPosition) * direction);
    };

    private _onPaneResizePointerUp = (e: PointerEvent) => {
        if (e.pointerId !== this.panePointerId) return;
        this._endPaneResize();
    };

    private _onPaneResizePointerCancel = (e: PointerEvent) => {
        if (e.pointerId !== this.panePointerId) return;
        this._endPaneResize();
    };

    private _endPaneResize() {
        if (!this.isResizingPanes) return;
        this.isResizingPanes = false;
        const pointerId = this.panePointerId;
        this.panePointerId = null;
        if (pointerId !== null && this.elements.resizeHandle?.hasPointerCapture(pointerId)) {
            this.elements.resizeHandle.releasePointerCapture(pointerId);
        }
        if (this.elements.previewFrameCover) this.elements.previewFrameCover.classList.add("swo-hidden");
        document.body.style.cursor = this.previousCursor;
        document.body.style.userSelect = this.previousUserSelect;
        if (UIManager.activeResizeManager === this) UIManager.activeResizeManager = null;
    }

    private _applyPaneSize(newEditorSize: number) {
        if (!this.elements.editorPane || !this.elements.resizeHandle || !this.elements.editorPane.parentElement) return;
        const parent = this.elements.editorPane.parentElement;
        const totalSize = this.isVertical ? parent.offsetHeight : parent.offsetWidth;
        const handleSize = this.isVertical ? this.elements.resizeHandle.offsetHeight : this.elements.resizeHandle.offsetWidth;
        if (totalSize <= handleSize) return;
        const minPixelSize = Math.min(Math.max(100, totalSize * 0.15), (totalSize - handleSize) / 2);

        newEditorSize = Math.max(minPixelSize, Math.min(newEditorSize, totalSize - minPixelSize - handleSize));
        this.elements.editorPane.style[this.isVertical ? "height" : "width"] = `${newEditorSize / totalSize * 100}%`;
        this.elements.resizeHandle.setAttribute("aria-valuenow", String(Math.round(newEditorSize / totalSize * 100)));
    }

    private _onPaneResizeKeyDown = (e: KeyboardEvent) => {
        if (!this.elements.editorPane) return;
        const step = e.shiftKey ? 64 : 16;
        const backwardKey = this.isVertical ? "ArrowUp" : "ArrowLeft";
        const forwardKey = this.isVertical ? "ArrowDown" : "ArrowRight";
        const decreaseKey = this.isReversed ? forwardKey : backwardKey;
        const increaseKey = this.isReversed ? backwardKey : forwardKey;
        if (e.key === decreaseKey || e.key === increaseKey) {
            const currentSize = this.isVertical ? this.elements.editorPane.offsetHeight : this.elements.editorPane.offsetWidth;
            this._applyPaneSize(currentSize + (e.key === decreaseKey ? -step : step));
            e.preventDefault();
        }
    };

    private _onPaneResizeDoubleClick = () => {
        if (!this.elements.editorPane) return;
        this.elements.editorPane.style[this.isVertical ? "height" : "width"] = "calc(50% - 0.25rem)";
        this.elements.resizeHandle?.setAttribute("aria-valuenow", "50");
    };

    // --- Console Resizing Logic ---
    private _onConsoleResizePointerDown = (e: PointerEvent) => {
        if (!this.elements.previewPaneContainer || !this.elements.previewFrameCover) return;
        if (e.button !== 0 || this.isResizingPanes || this.isResizingConsole) return;

        this._claimResize();
        e.preventDefault();
        this.isResizingConsole = true;
        this.consolePointerId = e.pointerId;
        this.previousCursor = document.body.style.cursor;
        this.previousUserSelect = document.body.style.userSelect;
        this._initialMouseYConsole = e.clientY;
        this._initialPreviewHeight = this.elements.previewPaneContainer.offsetHeight;
        this.elements.previewFrameCover.classList.remove("swo-hidden");
        document.body.style.cursor = "row-resize";
        document.body.style.userSelect = "none";
        try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { this._endConsoleResize(); }
    };

    private _onConsoleResizePointerMove = (e: PointerEvent) => {
        if (!this.isResizingConsole || e.pointerId !== this.consolePointerId) return;
        this._applyPreviewHeight(this._initialPreviewHeight + (e.clientY - this._initialMouseYConsole));
    };

    private _onConsoleResizePointerUp = (e: PointerEvent) => {
        if (e.pointerId !== this.consolePointerId) return;
        this._endConsoleResize();
    };

    private _onConsoleResizePointerCancel = (e: PointerEvent) => {
        if (e.pointerId !== this.consolePointerId) return;
        this._endConsoleResize();
    };

    private _endConsoleResize() {
        if (!this.isResizingConsole) return;
        this.isResizingConsole = false;
        const pointerId = this.consolePointerId;
        this.consolePointerId = null;
        if (pointerId !== null && this.elements.resizeHandleConsole?.hasPointerCapture(pointerId)) {
            this.elements.resizeHandleConsole.releasePointerCapture(pointerId);
        }
        if (this.elements.previewFrameCover) this.elements.previewFrameCover.classList.add("swo-hidden");
        document.body.style.cursor = this.previousCursor;
        document.body.style.userSelect = this.previousUserSelect;
        if (UIManager.activeResizeManager === this) UIManager.activeResizeManager = null;
    }

    private _applyPreviewHeight(newPreviewHeight: number) {
        if (!this.elements.rightPane || !this.elements.resizeHandleConsole || !this.elements.previewPaneContainer || !this.elements.consoleContainer) return;
        const totalHeight = this.elements.rightPane.offsetHeight;
        const handleHeight = this.elements.resizeHandleConsole.offsetHeight;
        const availableHeight = totalHeight - handleHeight;
        if (availableHeight <= 0) return;
        const minPaneHeight = Math.min(Math.max(50, totalHeight * 0.1), availableHeight / 2);

        newPreviewHeight = Math.max(minPaneHeight, Math.min(newPreviewHeight, totalHeight - minPaneHeight - handleHeight));
        const previewRatio = newPreviewHeight / availableHeight;
        this.elements.resizeHandleConsole.setAttribute("aria-valuenow", String(Math.round(previewRatio * 100)));
        this.elements.previewPaneContainer.style.height = `calc(${previewRatio * 100}% - ${handleHeight * previewRatio}px)`;
        this.elements.consoleContainer.style.height = `calc(${(1 - previewRatio) * 100}% - ${handleHeight * (1 - previewRatio)}px)`;
    }

    private _onConsoleResizeKeyDown = (e: KeyboardEvent) => {
        if (!this.elements.previewPaneContainer) return;
        const step = e.shiftKey ? 64 : 16;
        let handled = true;
        switch (e.key) {
            case "ArrowUp":
                this._applyPreviewHeight(this.elements.previewPaneContainer.offsetHeight - step);
                break;
            case "ArrowDown":
                this._applyPreviewHeight(this.elements.previewPaneContainer.offsetHeight + step);
                break;
            default:
                handled = false;
        }
        if (handled) e.preventDefault();
    };

    private _claimResize() {
        const previous = UIManager.activeResizeManager;
        if (previous && previous !== this) {
            previous._endPaneResize();
            previous._endConsoleResize();
        }
        UIManager.activeResizeManager = this;
    }

    // Teardown (F11): cancel any active drag and restore host interaction
    // state immediately, even if the editor is destroyed mid-resize.
    destroy() {
        this._endPaneResize();
        this._endConsoleResize();
        this.elements = {};
        // Element-level listeners are discarded with the UI DOM; the resize
        // implementation no longer attaches document-level listeners.
    }
}
